import React, { useState } from "react";
import Container from "@src/components/Container";
import { Box, Flex } from "@src/components/Boxes";
import FastFactsSection from "@src/components/partials/press/FastFactsSection";
import styled from "@emotion/styled";
import { mediaQueries } from "@src/styles/theme";
import BackgroundImage from "gatsby-background-image";
import { css } from "@emotion/react";
import Translate from "@src/components/translation/Translate";
import { graphql, useStaticQuery } from "gatsby";
import NewsBox from "@src/components/NewsBox";
import scrollTo from "gatsby-plugin-smoothscroll";

const anchorTitle = css`
  height: 0;
  display: block;
  position: relative;
  top: -88px;
  visibility: hidden;
`;

const QuoteText = styled("p")`
  font-weight: 300;
  line-height: 35px;
  margin: 0 0 25px;
  font-size: 21px;
  text-align: center;

  ${mediaQueries[0]} {
    font-size: 25px;
  }
`;

const QuoteAuthor = styled("footer")`
  color: rgba(255, 255, 255, 0.9);
  display: block;
  font-weight: 600;
  line-height: 1.42857;
  font-size: 17px;

  ${mediaQueries[0]} {
    font-size: 20px;
  }
`;

const InvestorsTitle = styled("h2")`
  font-size: 24px;
  font-weight: 800;
  line-height: 40px;
  margin-bottom: 25px;
  margin-top: 0;
  text-align: center;
  color: #fff;
`;

const Quote = styled("blockquote")`
  border-width: 0;
  font-size: 20px;
  margin: 0;
  text-align: center;
  padding: 30px;

  ${mediaQueries[0]} {
    padding: 50px 25px;
  }
`;

const QuoteBox = styled(BackgroundImage)`
  box-shadow: rgba(0, 0, 0, 0.15) 0 2px 4px 1px;
  border-radius: 5px;
  overflow: hidden;
`;

const SectionTitle = styled("h2")`
  font-size: 24px;
  font-weight: 800;
  line-height: 40px;
  margin-bottom: 52px;
  text-align: center;
  color: #363636;
`;

const DownloadBoxWrapper = styled("a")`
  cursor: pointer;
  padding: 0 5px;
  width: auto;
  display: flex;
  min-height: 1px;
  position: relative;
  text-align: center;

  &:hover {
    text-decoration: none;
  }

  ${mediaQueries[0]} {
    width: 33%;
  }
`;

const DownloadBox = styled("div")`
  background-color: #fff;
  border: 1px solid transparent;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(201, 201, 201, 0.5);
  color: #00bcf2;
  display: block;
  font-weight: 600;
  margin-bottom: 10px;
  min-height: 20px;
  padding: 19px;
  text-decoration: none;
  width: 100%;

  :hover {
    text-decoration: none;
    border-color: #00a8d9;
    color: #00a8d9;
    outline: 0;
  }

  & > span {
    color: #8c8c8c;
  }

  & > p {
    margin-bottom: 3px;
  }
`;

const LinkButton = styled.button`
  cursor: pointer;
  background-color: transparent;
  color: #00bcf2;
  font-weight: 600;
  text-decoration: none;
  text-wrap: none;
  white-space: nowrap;
  border: 0;
  &:hover {
    color: #00a8d9;
    outline: 0;
    text-decoration: none;
  }
  padding: 5px;
`;

function MainSection() {
  const data = useStaticQuery(graphql`
    query {
      blueStars: file(name: { eq: "light-blue-stars@2x" }) {
        childImageSharp {
          fluid(maxWidth: 920, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      purpleStars: file(name: { eq: "purple-stars@2x" }) {
        childImageSharp {
          fluid(maxWidth: 920, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }

      news: allFile(filter: { relativeDirectory: { in: "press/news" } }) {
        edges {
          node {
            publicURL
            name
            childImageSharp {
              fluid(maxWidth: 320, quality: 90) {
                ...GatsbyImageSharpFluid_noBase64
              }
            }
          }
        }
      }

      directus {
        page_press {
          press_assets
        }
      }
    }
  `);
  const {
    blueStars,
    purpleStars,
    news,
    directus: {
      page_press: { press_assets },
    },
  } = data;

  const [isMoreNews, setIsMoreNews] = useState(false);

  function getPicByName(name) {
    const item = news.edges.find((item) => item.node.name === name);
    return item.node;
  }

  return (
    <Box backgroundColor="#f5f5f3">
      <Flex
        flexWrap="wrap"
        justifyContent="center"
        padding="15px"
        backgroundColor="#fff"
        textAlign="center"
        css={css`
          border-bottom: 1px solid #e8e8e8;
          & > button {
            margin: 5px 20px;
          }
        `}
      >
        <LinkButton onClick={() => scrollTo("#awards")}>
          <Translate path="directus.page_press.link_fast_facts" />
        </LinkButton>
        <LinkButton onClick={() => scrollTo("#awards")}>
          <Translate path="directus.page_press.link_awards" />
        </LinkButton>
        <LinkButton onClick={() => scrollTo("#in-the-news")}>
          <Translate path="directus.page_press.link_news" />
        </LinkButton>
        <LinkButton onClick={() => scrollTo("#press-assets")}>
          <Translate path="directus.page_press.link_assets" />
        </LinkButton>
        <LinkButton onClick={() => scrollTo("#contact-us")}>
          <Translate path="directus.page_press.link_contact" />
        </LinkButton>
      </Flex>

      <Container>
        <Box py="60px">
          <QuoteBox
            fluid={blueStars.childImageSharp.fluid}
            css={css`
              background-position: center top !important;
              background-size: 991px 296px !important;
              background-color: #00a4ed;
              color: #fff;
            `}
          >
            <Flex width={["100%", "83%"]} mx="auto" flexDirection="column" alignItems="center">
              <Quote>
                <QuoteText>
                  <Translate path="directus.page_press.quote_text" />
                </QuoteText>
                <QuoteAuthor>
                  <Translate path="directus.page_press.quote_signature" />
                </QuoteAuthor>
              </Quote>
            </Flex>
          </QuoteBox>
        </Box>

        <FastFactsSection />

        <Flex
          paddingBottom="60px"
          width={["100%", "100%", "100%", "83%"]}
          mx="auto"
          flexDirection="column"
          alignItems="center"
          id="in-the-news"
        >
          <SectionTitle>
            <Translate path="directus.page_press.news_title" />
          </SectionTitle>
          <Flex flexWrap="wrap" width="100%">
            <NewsBox
              img={getPicByName("2020-recovered-education")}
              text="components.in_the_news.88.title"
              source="components.in_the_news.88.source"
              date="components.in_the_news.88.date"
              url="https://www.ozy.com/pg/newsletter/the-daily-dose/413606/"
            />

            <NewsBox
              img={getPicByName("language-barriers@2x")}
              text="components.in_the_news.85.title"
              source="components.in_the_news.85.source"
              date="components.in_the_news.85.date"
              url="https://www.educationdive.com/news/survey-more-than-half-of-us-teachers-concerned-about-language-barriers-wit/544191/"
            />

            <NewsBox
              img={getPicByName("Teachers-Need-More-of-a-Say@2x")}
              text="components.in_the_news.84.title"
              source="components.in_the_news.84.source"
              date="components.in_the_news.84.date"
              url="https://www.primaryleaders.com/school-procurement/ict/why-teachers-need-more-of-a-say-in-edtech"
            />

            <NewsBox
              img={getPicByName("4-Things@2x")}
              text="components.in_the_news.83.title"
              source="components.in_the_news.83.source"
              date="components.in_the_news.83.date"
              url="https://www.workingmother.com/4-things-teachers-want-working-moms-to-know-about-communicating-about-school-straight-from-teacher"
            />

            <NewsBox
              img={getPicByName("Four-Strategies@2x")}
              text="components.in_the_news.82.title"
              source="components.in_the_news.82.source"
              date="components.in_the_news.82.date"
              url="https://www.gettingsmart.com/2018/11/four-strategies-for-introducing-empathy-in-the-classroom/"
            />

            <NewsBox
              img={getPicByName("App-Helps@2x")}
              text="components.in_the_news.81.title"
              source="components.in_the_news.81.source"
              date="components.in_the_news.81.date"
              url="https://www.nbcchicago.com/multimedia/app-helps-parents-teachers-break-down-language-barrier-500633962.html?akmobile=o&appVideoHub=y"
            />

            {isMoreNews && (
              <>
                <NewsBox
                  img={getPicByName("Aplicacion-ayuda@2x")}
                  text="components.in_the_news.80.title"
                  source="components.in_the_news.80.source"
                  date="components.in_the_news.80.date"
                  url="https://www.telemundochicago.com/noticias/local/Aplicaci_n-ayuda-a-padres-a-comunicarse-con-los-maestro_TLMD---Chicago-500642511.html?akmobile=o&appVideoHub=y"
                />

                <NewsBox
                  img={getPicByName("First-Monetization-Feature@2x")}
                  text="components.in_the_news.79.title"
                  source="components.in_the_news.79.source"
                  date="components.in_the_news.79.date"
                  url="https://www.edsurge.com/news/2018-11-15-classdojo-goes-beyond-school-to-launch-first-monetization-feature-for-parents"
                />

                <NewsBox
                  img={getPicByName("The-words@2x")}
                  text="components.in_the_news.77.title"
                  source="components.in_the_news.77.source"
                  date="components.in_the_news.77.date"
                  url="https://www.bbc.com/news/education-45450648"
                />

                <NewsBox
                  img={getPicByName("Parents-Would-Like-To-Hear@2x")}
                  text="components.in_the_news.76.title"
                  source="components.in_the_news.76.source"
                  date="components.in_the_news.76.date"
                  url="https://uk.style.yahoo.com/top-10-things-parents-hear-095732218.html?guccounter=1"
                />

                <NewsBox
                  img={getPicByName("Forgetting@2x")}
                  text="components.in_the_news.75.title"
                  source="components.in_the_news.75.source"
                  date="components.in_the_news.75.date"
                  url="https://www.the74million.org/article/boser-frank-forgetting-can-be-an-important-part-of-learning-how-summer-slide-and-the-process-of-relearning-can-actually-help-students-master-a-subject/"
                />

                <NewsBox
                  img={getPicByName("Conundrums@2x")}
                  text="components.in_the_news.74.title"
                  source="components.in_the_news.74.source"
                  date="components.in_the_news.74.date"
                  url="https://www.businessinsider.com/elon-musk-ad-astra-classdojo-conundrum-ethics-puzzles-2018-8"
                />

                <NewsBox
                  img={getPicByName("Teaching-Empathy@2x")}
                  text="components.in_the_news.73.title"
                  source="components.in_the_news.73.source"
                  date="components.in_the_news.73.date"
                  url="https://www.nytimes.com/2018/08/02/education/learning/bulletin-board.html"
                />

                <NewsBox
                  img={getPicByName("Teaching-Empathy_1@2x")}
                  text="components.in_the_news.72.title"
                  source="components.in_the_news.72.source"
                  date="components.in_the_news.72.date"
                  url="https://www.the74million.org/article/teaching-empathy-in-kindergarten-or-do-we-have-to-let-the-girls-play-too/"
                />

                <NewsBox
                  img={getPicByName("eschool")}
                  text="components.in_the_news.71.title"
                  source="components.in_the_news.71.source"
                  date="components.in_the_news.71.date"
                  url="https://www.eschoolnews.com/2018/04/04/power-online-communities-teachers/"
                />

                <NewsBox
                  img={getPicByName("nyt")}
                  text="components.in_the_news.70.title"
                  source="components.in_the_news.70.source"
                  date="components.in_the_news.70.date"
                  url="https://www.nytimes.com/2018/04/05/education/learning/bulletin-board.html"
                />

                <NewsBox
                  img={getPicByName("edu")}
                  text="components.in_the_news.69.title"
                  source="components.in_the_news.69.source"
                  date="components.in_the_news.69.date"
                  url="http://educationpost.org/this-app-is-connecting-classrooms-around-the-globe/"
                />

                <NewsBox
                  img={getPicByName("gettingsmart2")}
                  text="components.in_the_news.68.title"
                  source="components.in_the_news.68.source"
                  date="components.in_the_news.68.date"
                  url="http://www.gettingsmart.com/2018/02/how-one-teacher-is-teaching-her-students-to-be-kind/"
                />

                <NewsBox
                  img={getPicByName("ycomb")}
                  text="components.in_the_news.67.title"
                  source="components.in_the_news.67.source"
                  date="components.in_the_news.67.date"
                  url="https://blog.ycombinator.com/its-surprising-how-much-small-teams-can-get-done-sam-chaudhary-of-classdojo/"
                />

                <NewsBox
                  img={getPicByName("huffpost-alt@2x")}
                  text="components.in_the_news.66.title"
                  source="components.in_the_news.66.source"
                  date="components.in_the_news.66.date"
                  url="https://www.huffingtonpost.com/entry/give-homework-an-f_us_5a550f87e4b0ee59d41c0e6d"
                />

                <NewsBox
                  img={getPicByName("edutopia")}
                  text="components.in_the_news.65.title"
                  source="components.in_the_news.65.source"
                  date="components.in_the_news.65.date"
                  url="https://www.edutopia.org/article/parent-engagement-digital-age"
                />

                <NewsBox
                  img={getPicByName("gettingsmart")}
                  text="components.in_the_news.64.title"
                  source="components.in_the_news.64.source"
                  date="components.in_the_news.64.date"
                  url="http://www.gettingsmart.com/2017/11/using-technology-to-build-partnerships-with-parents/"
                />

                <NewsBox
                  img={getPicByName("london@2x")}
                  text="components.in_the_news.63.title"
                  source="components.in_the_news.63.source"
                  date="components.in_the_news.63.date"
                  url="https://www.standard.co.uk/lifestyle/london-life/classdojo-the-social-network-linking-up-schools-with-parents-a3651366.html"
                />

                <NewsBox
                  img={getPicByName("digital-ed@2x")}
                  text="components.in_the_news.62.title"
                  source="components.in_the_news.62.source"
                  date="components.in_the_news.62.date"
                  url="http://blogs.edweek.org/edweek/DigitalEducation/2017/09/hurricane_harvey_shuttered_school_not_teaching.html"
                />

                <NewsBox
                  img={getPicByName("huffpost-student@2x")}
                  text="components.in_the_news.61.title"
                  source="components.in_the_news.61.source"
                  date="components.in_the_news.61.date"
                  url="https://www.huffingtonpost.com/entry/59b6bab7e4b0e1d93779041a"
                />

                <NewsBox
                  img={getPicByName("wa-pa@2x")}
                  text="components.in_the_news.60.title"
                  source="components.in_the_news.60.source"
                  date="components.in_the_news.60.date"
                  url="https://www.washingtonpost.com/news/parenting/wp/2017/09/11/a-mindful-start-to-the-school-year-four-back-to-school-questions-to-build-emotional-intelligence/?utm_term=.07dba471057c"
                />

                <NewsBox
                  img={getPicByName("parentmag@2x")}
                  text="components.in_the_news.59.title"
                  source="components.in_the_news.59.source"
                  date="components.in_the_news.59.date"
                  url="http://www.parents.com/parenting/technology/tech-savvy-ways-to-connect-with-your-kids-classroom/"
                />

                <NewsBox
                  img={getPicByName("huffpost-alt@2x")}
                  text="components.in_the_news.58.title"
                  source="components.in_the_news.58.source"
                  date="components.in_the_news.58.date"
                  url="https://www.huffingtonpost.com/entry/back-to-school-5-awesome-products-students-and-parents_us_59a07bc6e4b0d0ef9f1c1394"
                />

                <NewsBox
                  img={getPicByName("ed-week@2x")}
                  text="components.in_the_news.57.title"
                  source="components.in_the_news.57.source"
                  date="components.in_the_news.57.date"
                  url="http://blogs.edweek.org/teachers/teaching_now/2017/08/teacher_input_edtech.html"
                />

                <NewsBox
                  img={getPicByName("hechinger-alt@2x")}
                  text="components.in_the_news.56.title"
                  source="components.in_the_news.56.source"
                  date="components.in_the_news.56.date"
                  url="http://hechingerreport.org/teacher-voice-classroom-loud-one-students-thriving/"
                />

                <NewsBox
                  img={getPicByName("abc@2x")}
                  text="components.in_the_news.55.title"
                  source="components.in_the_news.55.source"
                  date="components.in_the_news.55.date"
                  url="http://www.kgun9.com/news/local-news/back-to-school-apps-every-parent-needs"
                />

                <NewsBox
                  img={getPicByName("kentucky@2x")}
                  text="components.in_the_news.54.title"
                  source="components.in_the_news.54.source"
                  date="components.in_the_news.54.date"
                  url="https://www.kentuckyliving.com/lifestyle/building-character"
                />

                <NewsBox
                  img={getPicByName("Inc-a@2x")}
                  text="components.in_the_news.53.title"
                  source="components.in_the_news.53.source"
                  date="components.in_the_news.53.date"
                  url="https://www.inc.com/profile/classdojo"
                />

                <NewsBox
                  img={getPicByName("Forbes-a@2x")}
                  text="components.in_the_news.52.title"
                  source="components.in_the_news.52.source"
                  date="components.in_the_news.52.date"
                  url="https://www.forbes.com/sites/kathleenchaykowski/2017/05/22/how-classdojo-built-one-of-the-most-popular-classroom-apps-by-listening-to-teachers/#2d7362491e5e"
                />

                <NewsBox
                  img={getPicByName("CNN-a@2x")}
                  text="components.in_the_news.51.title"
                  source="components.in_the_news.51.source"
                  date="components.in_the_news.51.date"
                  url="https://money.cnn.com/2017/05/24/smallbusiness/classdojo-education-app-behavior/index.html"
                />

                <NewsBox
                  img={getPicByName("Hechinger-a@2x")}
                  text="components.in_the_news.50.title"
                  source="components.in_the_news.50.source"
                  date="components.in_the_news.50.date"
                  url="http://hechingerreport.org/teacher-voice-working-indiana-school-used-shy-student-use-technology-help-timid-first-graders-speak/"
                />

                <NewsBox
                  img={getPicByName("2017-05-hechinger@2x")}
                  text="components.in_the_news.49.title"
                  source="components.in_the_news.49.source"
                  date="components.in_the_news.49.date"
                  url="http://hechingerreport.org/mindful-tech-tool-can-help/"
                />

                <NewsBox
                  img={getPicByName("2017-05-edsurge@2x")}
                  text="components.in_the_news.48.title"
                  source="components.in_the_news.48.source"
                  date="components.in_the_news.48.date"
                  url="https://www.edsurge.com/news/2017-05-09-classdojo-and-yale-team-up-to-bring-mindfulness-to-the-masses"
                />

                <NewsBox
                  img={getPicByName("2017-05-the74@2x")}
                  text="components.in_the_news.47.title"
                  source="components.in_the_news.47.source"
                  date="components.in_the_news.47.date"
                  url="https://www.the74million.org/article/classdojo-yale-roll-out-new-video-series-on-mindfulness-to-help-young-students-tame-the-beast"
                />

                <NewsBox
                  img={getPicByName("2017-05-usatoday@2x")}
                  text="components.in_the_news.46.title"
                  source="components.in_the_news.46.source"
                  date="components.in_the_news.46.date"
                  url="https://www.usatoday.com/story/news/2017/05/08/mindfulness-schools/101447370/"
                />

                <NewsBox
                  img={getPicByName("2017-05-educationdive@2x")}
                  text="components.in_the_news.45.title"
                  source="components.in_the_news.45.source"
                  date="components.in_the_news.45.date"
                  url="http://www.educationdive.com/news/yale-program-partners-with-app-to-promote-mindfulness-in-schools/442164/"
                />

                <NewsBox
                  img={getPicByName("2017-04-hechinger@2x")}
                  text="components.in_the_news.44.title"
                  source="components.in_the_news.44.source"
                  date="components.in_the_news.44.date"
                  url="http://hechingerreport.org/opinion-biggest-tech-trendsetter-know-just-may-local-teacher/"
                />

                <NewsBox
                  img={getPicByName("2017-04-edsurge@2x")}
                  text="components.in_the_news.43.title"
                  source="components.in_the_news.43.source"
                  date="components.in_the_news.43.date"
                  url="https://www.edsurge.com/news/2017-04-17-these-4-quirky-unorthodox-letters-got-people-hired-at-edtech-startups"
                />

                <NewsBox
                  img={getPicByName("inc_092016@2x")}
                  text="components.in_the_news.42.title"
                  source="components.in_the_news.42.source"
                  date="components.in_the_news.42.date"
                  url="http://www.bizjournals.com/sanfrancisco/news/2017/03/30/classdojo-liam-don-sam-chaudhary-platform-k-8.html"
                />

                <NewsBox
                  img={getPicByName("2017-03-gettingsmart@2x")}
                  text="components.in_the_news.41.title"
                  source="components.in_the_news.41.source"
                  date="components.in_the_news.41.date"
                  url="http://www.gettingsmart.com/2017/03/how-to-find-your-teacher-next-door/"
                />

                <NewsBox
                  img={getPicByName("2017-03-huffpo@2x")}
                  text="components.in_the_news.40.title"
                  source="components.in_the_news.40.source"
                  date="components.in_the_news.40.date"
                  url="http://www.huffingtonpost.com/entry/58c30742e4b0c3276fb7849a"
                />

                <NewsBox
                  img={getPicByName("2017-02-hechinger@2x")}
                  text="components.in_the_news.39.title"
                  source="components.in_the_news.39.source"
                  date="components.in_the_news.39.date"
                  url="http://hechingerreport.org/detroit-area-kindergarten-teacher-ensures-children-learn-empathy-age-divisiveness/"
                />

                <NewsBox
                  img={getPicByName("2017-02-theguardian@2x")}
                  text="components.in_the_news.38.title"
                  source="components.in_the_news.38.source"
                  date="components.in_the_news.38.date"
                  url="https://www.theguardian.com/teacher-network/2017/feb/24/parents-involved-school-teaching-engagement"
                />

                <NewsBox
                  img={getPicByName("2017-02-gettingsmart@2x")}
                  text="components.in_the_news.37.title"
                  source="components.in_the_news.37.source"
                  date="components.in_the_news.37.date"
                  url="http://www.gettingsmart.com/2017/02/every-10-year-old-should-know-maslows-hierarchy/"
                />

                <NewsBox
                  img={getPicByName("2016-12-chalkbeat@2x")}
                  text="components.in_the_news.36.title"
                  source="components.in_the_news.36.source"
                  date="components.in_the_news.36.date"
                  url="http://www.chalkbeat.org/posts/tn/2016/12/08/why-i-take-class-time-to-teach-perseverance-and-let-my-fourth-graders-write-on-their-desks/"
                />

                <NewsBox
                  img={getPicByName("2016-12-huffpo@2x")}
                  text="components.in_the_news.35.title"
                  source="components.in_the_news.35.source"
                  date="components.in_the_news.35.date"
                  url="http://www.huffingtonpost.com/entry/583e10dee4b048862d73fc39?timestamp=1480962873849"
                />

                <NewsBox
                  img={getPicByName("2016-11-thebookmark@2x")}
                  text="components.in_the_news.34.title"
                  source="components.in_the_news.34.source"
                  date="components.in_the_news.34.date"
                  url="http://verizoninternet.redoriginproxy.com/bookmark/guides/tech-tools-for-teachers/"
                />

                <NewsBox
                  img={getPicByName("FastCo90percent@2x")}
                  text="components.in_the_news.1.title"
                  source="components.in_the_news.1.source"
                  date="components.in_the_news.1.date"
                  url="https://www.fastcompany.com/3065654/innovation-agents/classdojo-is-teaching-kids-empathy-in-90-of-schools-nationwide"
                />

                <NewsBox
                  img={getPicByName("parent_herald@2x")}
                  text="components.in_the_news.2.title"
                  source="components.in_the_news.2.source"
                  date="components.in_the_news.2.date"
                  url="http://www.parentherald.com/articles/79384/20161031/importance-teaching-empathy-how-can-technology-teach-kids-some-most-significant-social-emotional-skills.htm"
                />

                <NewsBox
                  img={getPicByName("huffington_class@2x")}
                  text="components.in_the_news.3.title"
                  source="components.in_the_news.3.source"
                  date="components.in_the_news.3.date"
                  url="http://www.huffingtonpost.com/entry/empathy-videos_us_57f6691ee4b002a731210e7a?section=us_parents"
                />

                <NewsBox
                  img={getPicByName("huff_hands")}
                  text="components.in_the_news.4.title"
                  source="components.in_the_news.4.source"
                  date="components.in_the_news.4.date"
                  url="http://www.huffingtonpost.com/entry/58067f80e4b08ddf9ece114e?timestamp=1477062336129"
                />

                <NewsBox
                  img={getPicByName("fastco_app@2x")}
                  text="components.in_the_news.5.title"
                  source="components.in_the_news.5.source"
                  date="components.in_the_news.5.date"
                  url="https://www.fastcodesign.com/3064130/the-35-most-innovative-apps-of-the-year"
                />

                <NewsBox
                  img={getPicByName("inc_092016@2x")}
                  text="components.in_the_news.6.title"
                  source="components.in_the_news.6.source"
                  date="components.in_the_news.6.date"
                  url="http://www.inc.com/salvador-rodriguez/classdojo-monetization-slack-classrooms.html"
                />

                <NewsBox
                  img={getPicByName("Quartz092716@2x")}
                  text="components.in_the_news.7.title"
                  source="components.in_the_news.7.source"
                  date="components.in_the_news.7.date"
                  url="http://qz.com/791388/meet-mojo-a-monster-stanford-helped-create-to-teach-your-kids-that-effort-matters-more-than-iq/"
                />

                <NewsBox
                  img={getPicByName("USAToday091316@2x")}
                  text="components.in_the_news.8.title"
                  source="components.in_the_news.8.source"
                  date="components.in_the_news.8.date"
                  url="http://www.usatoday.com/story/tech/news/2016/09/13/tech-hungry-teachers-transform-classroom/89941852/"
                />

                <NewsBox
                  img={getPicByName("huff_letter@2x")}
                  text="components.in_the_news.9.title"
                  source="components.in_the_news.9.source"
                  date="components.in_the_news.9.date"
                  url="http://www.huffingtonpost.com/entry/why-i-did-it-the-no-homework-letter_us_57c5b5bfe4b004ff0420daf3?zinfzyzcntvwe9udi"
                />

                <NewsBox
                  img={getPicByName("usnews_class@2x")}
                  text="components.in_the_news.10.title"
                  source="components.in_the_news.10.source"
                  date="components.in_the_news.10.date"
                  url="http://www.usnews.com/news/articles/2016-08-08/kids-want-to-go-to-this-summer-school"
                />

                <NewsBox
                  img={getPicByName("the74million@2x")}
                  text="components.in_the_news.11.title"
                  source="components.in_the_news.11.source"
                  date="components.in_the_news.11.date"
                  url="https://www.the74million.org/article/classdojo-announces-the-launch-of-studentstories-a-new-way-for-students-to-share-their-classwork"
                />

                <NewsBox
                  img={getPicByName("BusinessInsider070816@2x")}
                  text="components.in_the_news.12.title"
                  source="components.in_the_news.12.source"
                  date="components.in_the_news.12.date"
                  url="http://www.businessinsider.com/ed-tech-startup-classdojo-is-going-viral-2016-7"
                />

                <NewsBox
                  img={getPicByName("presshere@2x")}
                  text="components.in_the_news.13.title"
                  source="components.in_the_news.13.source"
                  date="components.in_the_news.13.date"
                  url="http://www.pressheretv.com/classdojo/"
                />

                <NewsBox
                  img={getPicByName("mercury_dojo_office@2x")}
                  text="components.in_the_news.14.title"
                  source="components.in_the_news.14.source"
                  date="components.in_the_news.14.date"
                  url="http://www.mercurynews.com/2016/06/15/qa-classdojo-ceo-and-co-founder-sam-chaudhary-on-improving-education-from-the-ground-up/"
                />

                <NewsBox
                  img={getPicByName("incLetters@2x")}
                  text="components.in_the_news.15.title"
                  source="components.in_the_news.15.source"
                  date="components.in_the_news.15.date"
                  url="http://www.inc.com/nina-ojeda/how-having-kids-%E2%80%8Bcan%E2%80%8B-make-you-a-better-entrepreneur.html"
                />

                <NewsBox
                  img={getPicByName("forbesHands@2x")}
                  text="components.in_the_news.16.title"
                  source="components.in_the_news.16.source"
                  date="components.in_the_news.16.date"
                  url="http://www.forbes.com/sites/schoolboard/2016/05/10/if-you-want-to-improve-education-you-need-to-unscale-it/#5ccfca05351e"
                />

                <NewsBox
                  img={getPicByName("hechingerPress@2x")}
                  text="components.in_the_news.17.title"
                  source="components.in_the_news.17.source"
                  date="components.in_the_news.17.date"
                  url="http://hechingerreport.org/organic-adoption-one-classroom-technology-leads-seamless-way-share-ideas/"
                />

                <NewsBox
                  img={getPicByName("curious@2x")}
                  text="components.in_the_news.18.title"
                  source="components.in_the_news.18.source"
                  date="components.in_the_news.18.date"
                  url="https://curious.com/conversations/empowering-teachers-in-the-classroom/"
                />

                <NewsBox
                  img={getPicByName("tc@2x")}
                  text="components.in_the_news.19.title"
                  source="components.in_the_news.19.source"
                  date="components.in_the_news.19.date"
                  url="http://techcrunch.com/2016/04/15/classdojo-raises-21-million-for-app-to-make-parent-teacher-meetings-obsolete/"
                />

                <NewsBox
                  img={getPicByName("eschool@2x")}
                  text="components.in_the_news.20.title"
                  source="components.in_the_news.20.source"
                  date="components.in_the_news.20.date"
                  url="http://www.eschoolnews.com/2016/04/18/classdojo-expands-beyond-classrooms-to-whole-school-sharing/"
                />

                <NewsBox
                  img={getPicByName("parents-in-classroom@2x")}
                  text="components.in_the_news.21.title"
                  source="components.in_the_news.21.source"
                  date="components.in_the_news.21.date"
                  url="http://www.dispatch.com/content/stories/local/2016/01/17/central-ohio-teachers-use-app-to-connect-with-students-parents.html"
                />

                <NewsBox
                  img={getPicByName("growthmindset03@2x")}
                  text="components.in_the_news.22.title"
                  source="components.in_the_news.22.source"
                  date="components.in_the_news.22.date"
                  url="http://www.usatoday.com/story/news/2016/01/19/growth-mindset-videos/79026138/"
                />

                <NewsBox
                  img={getPicByName("growthmindset01@2x")}
                  text="components.in_the_news.23.title"
                  source="components.in_the_news.23.source"
                  date="components.in_the_news.23.date"
                  url="http://ww2.kqed.org/mindshift/2016/01/19/what-classdojo-monsters-can-teach-kids-about-growth-mindset/"
                />

                <NewsBox
                  img={getPicByName("growthmindset02@2x")}
                  text="components.in_the_news.24.title"
                  source="components.in_the_news.24.source"
                  date="components.in_the_news.24.date"
                  url="https://www.edsurge.com/news/2016-01-19-classdojo-s-critters-want-to-teach-you-about-growth-mindsets"
                />

                <NewsBox
                  img={getPicByName("classstory@2x")}
                  text="components.in_the_news.25.title"
                  source="components.in_the_news.25.source"
                  date="components.in_the_news.25.date"
                  url="http://venturebeat.com/2015/09/05/classdojo-launches-new-tool-to-simplify-parent-teacher-communications/"
                />

                <NewsBox
                  img={getPicByName("founders@2x")}
                  text="components.in_the_news.26.title"
                  source="components.in_the_news.26.source"
                  date="components.in_the_news.26.date"
                  url="http://www.inc.com/laura-montini/2015-30-under-30-classdojo.html"
                />

                <NewsBox
                  img={getPicByName("crunchiewinner@2x")}
                  text="components.in_the_news.27.title"
                  source="components.in_the_news.27.source"
                  date="components.in_the_news.27.date"
                  url="http://techcrunch.com/video/classdojo-2015-best-education-startup-winner/518637766/"
                />

                <NewsBox
                  img={getPicByName("StudentPrivacy@2x")}
                  text="components.in_the_news.28.title"
                  source="components.in_the_news.28.source"
                  date="components.in_the_news.28.date"
                  url="http://blogs.wsj.com/digits/2015/01/12/white-house-moves-to-protect-data-privacy/"
                />

                <NewsBox
                  img={getPicByName("monett@2x")}
                  text="components.in_the_news.29.title"
                  source="components.in_the_news.29.source"
                  date="components.in_the_news.29.date"
                  url="http://www.monett-times.com/story/2138994.html"
                />

                <NewsBox
                  img={getPicByName("bett@2x")}
                  text="components.in_the_news.30.title"
                  source="components.in_the_news.30.source"
                  date="components.in_the_news.30.date"
                  url="https://pando.com/2014/03/12/the-edtech-startup-thats-shucking-the-playbook-by-acting-like-a-consumer-company/"
                />

                <NewsBox
                  img={getPicByName("1BillionPoints@2x")}
                  text="components.in_the_news.31.title"
                  source="components.in_the_news.31.source"
                  date="components.in_the_news.31.date"
                  url="https://www.edsurge.com/n/2014-03-12-one-billion-points-for-classdojo"
                />

                <NewsBox
                  img={getPicByName("messagingwars@2x")}
                  text="components.in_the_news.32.title"
                  source="components.in_the_news.32.source"
                  date="components.in_the_news.32.date"
                  url="http://www.fastcolabs.com/3028333/why-education-startup-classdojo-is-entering-the-messaging-app-wars"
                />

                <NewsBox
                  img={getPicByName("30Under30award@2x")}
                  text="components.in_the_news.33.title"
                  source="components.in_the_news.33.source"
                  date="components.in_the_news.33.date"
                  url="http://www.forbes.com/sites/jjcolao/2013/09/19/how-was-school-today-classroom-software-looks-to-make-that-question-history/#2715e4857a0b69c8eca02b00"
                />
              </>
            )}
          </Flex>
          <LinkButton onClick={() => setIsMoreNews(!isMoreNews)}>
            {!isMoreNews && <Translate path="pages.press.news_section.see_more" />}
            {isMoreNews && <Translate path="pages.press.news_section.see_less" />}
          </LinkButton>
        </Flex>

        <Box paddingBottom="60px">
          <QuoteBox
            fluid={purpleStars.childImageSharp.fluid}
            css={css`
              background-position: center top !important;
              background-size: 991px 296px !important;
              background-color: #595b78;
              color: #fff;
            `}
          >
            <Flex width={["100%", "83%"]} mx="auto" flexDirection="column" alignItems="center">
              <Quote>
                <InvestorsTitle>
                  <Translate path="directus.page_press.investors_title" />
                </InvestorsTitle>
                <QuoteText>
                  <Translate path="directus.page_press.investors_list" />
                </QuoteText>
              </Quote>
            </Flex>
          </QuoteBox>
        </Box>

        <Flex
          paddingBottom="60px"
          width={["100%", "100%", "100%", "83%"]}
          mx="auto"
          flexDirection="column"
          alignItems="center"
        >
          <div id="press-assets" css={anchorTitle}>
            <Translate path="directus.page_press.press_title" />
          </div>
          <SectionTitle>
            <Translate path="directus.page_press.press_title" />
          </SectionTitle>
          <Flex flexWrap="wrap" width="100%">
            {press_assets.map((asset, index) => (
              <DownloadBoxWrapper key={`asset-${index}`} href={asset.link}>
                <DownloadBox>
                  <p>
                    <Translate path={`directus.page_press.press_assets.text_${index + 1}`} />
                  </p>
                  <span>{asset.size}</span>
                </DownloadBox>
              </DownloadBoxWrapper>
            ))}
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
}

export default MainSection;
